<template>
  <div>
    <div class="supplier-select-methods button-group">
      <gl-button
        class="button-group__button"
        :primary="supplierSelectMethod === 'keyword'"
        @click="toggleSupplierSelect('keyword')"
        >あいまい検索</gl-button
      >
      <gl-button
        class="button-group__button"
        :primary="supplierSelectMethod === 'code'"
        @click="toggleSupplierSelect('code')"
        >コード入力</gl-button
      >
    </div>
    <div class="suppliers-container" :class="{'no-supplier': suppliers.length === 0 }">
      <supplier-list
        :suppliers="suppliers"
        @update:suppliers="$emit('update:suppliers', $event)"
        deletable
      />
    </div>
    <div class="actions button-group">
      <div class="left"></div>
      <span
        class="supplier-count"
        :class="{ 'exceeds-limit': suppliers.length > maxSuppliers }"
        >{{ supplierCount }} / {{ maxSuppliers }} 取引先</span
      >
      <gl-button class="button-group__button" raised @click="removeAll"
        >全削除</gl-button
      >
    </div>

    <!-- 取引先検索 -->
    <md-dialog
      class="supplier-select-dialog"
      :md-active="!!supplierSelectComponent"
      @md-clicked-outside="supplierSelectMethod = undefined"
    >
      <md-dialog-content>
        <component
          :is="supplierSelectComponent"
          @selected="addSuppliers"
        />
        <gl-button
          class="button-dialog-close"
          icon-button
          @click="supplierSelectMethod = undefined"
          ><md-icon>cancel</md-icon></gl-button
        >
      </md-dialog-content>
    </md-dialog>
  </div>
</template>

<script>
import SupplierList from '@/components/SupplierList.vue'
import SupplierSelectByKeyword from '@/components/SupplierSelectByKeyword'
import SupplierSelectByCode from '@/components/SupplierSelectByCode'

const MAX_SUPPLIERS = process.env.VUE_APP_MAX_SUPPLIERS
  ? Number(process.env.VUE_APP_MAX_SUPPLIERS)
  : 50

/**
 * 取引先選択コンポーネント
 */
export default {
  components: {
    SupplierList,
    SupplierSelectByKeyword,
    SupplierSelectByCode,
  },
  props: {
    /**
     * 分析対象取引先一覧
     */
    suppliers: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      /**
       * 選択可能な最大取引先数
       */
      maxSuppliers: MAX_SUPPLIERS,
      /**
       * 取引先選択方式
       *
       * 曖昧検索や取引先コードでの検索などを指定します。通常は検索用コンポーネントを
       * 子コンポーネントとして、子は`selected`イベントを発行することでこのコンポーネント
       * の選択取引先への追加を行ってもらいます。なので、検索用コンポーネントは`selected`
       * イベントで取引先リストを送りさえすれば、このコンポーネントの取引先検索用コンポーネント
       * として成り立ちます。
       */
      supplierSelectMethod: undefined,
    }
  },
  watch: {
    suppliers: {
      immediate: true,
      handler(suppliers) {
        const errors = []
        if (suppliers.length > this.maxSuppliers) {
          errors.push(`選択できる取引先数は${this.maxSuppliers}取引先までです`)
        }
        this.$emit('update:errors', errors)
      },
    },
  },
  computed: {
    /**
     * 取引先選択コンポーネント
     *
     * 取引先選択方式に対応する取引先選択コンポーネントを取得します。
     * 画面描画のいらないコンポーネント（e.g. ファイル入力）はここで対応する
     * 必要はありません。
     */
    supplierSelectComponent() {
      switch (this.supplierSelectMethod) {
        case 'keyword':
          return SupplierSelectByKeyword
        case 'code':
          return SupplierSelectByCode
      }
      return undefined
    },
    /**
     * 選択されている取引先数
     */
    supplierCount() {
      return this.suppliers.length
    },
  },
  methods: {
    /**
     * 取引先選択コンポーネント切替
     */
    toggleSupplierSelect(method) {
      if (this.supplierSelectMethod === method) {
        this.supplierSelectMethod = undefined
      } else {
        this.supplierSelectMethod = method
      }
    },
    /**
     * 取引先一覧クリア
     */
    removeAll() {
      this.$emit('update:suppliers', [])
    },
    /**
     * 取引先の追加
     */
    addSuppliers(suppliers) {
      const newSupplierCodes = suppliers.map(i =>
        [i.compnayCode, i.supplierCode].join(','),
      )

      // TODO パフォーマンスで課題が出たら修正
      this.$emit('update:suppliers', [
        ...this.suppliers.filter(
          i =>
            !newSupplierCodes.includes(
              [i.compnayCode, i.supplierCode].join(','),
            ),
        ),
        ...suppliers,
      ])

      this.supplierSelectMethod = undefined
    },
  },
}
</script>

<style lang="scss" scoped>
.suppliers-container {
  position: relative;
  height: 250px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.no-supplier {
  background: rgba(0, 0, 0, 0.12);

  &::after {
    font-family: 'Material Icons';
    content: 'NO SUPPLIERS';
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    font-size: 58px;
    color: rgba(0, 0, 0, 0.12);
  }
}

.supplier-select-dialog {
  min-width: 700px;
}

.button-dialog-close {
  position: fixed;
  top: 6px;
  right: 0;
}

.supplier-select-methods,
.supplier-select,
.suppliers-container {
  margin-top: var(--spacing-medium);
}

.supplier-select {
  padding-left: calc(var(--spacing-large) * 2);
}

.actions {
  width: 100%;
  text-align: right;
  display: flex;
  align-items: center;

  .left {
    flex: 1;
  }
}

.supplier-count {
  margin-right: var(--spacing-medium);
}

.exceeds-limit {
  color: var(--color-warn);
}
</style>
